import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { mutate } from "swr";
import { Card, CardContent } from "@mui/material";
import useGlobalContext from "../../hooks/useGlobalContext";
import PageTitleSubtitle from "../../components/PageTitleSubTitle";
import SearcherAndAdd from "../../components/SearcherAndAdd";
import PagePagination from "../../components/PagePagination";
import localize from "../../common/localize";
import ActionButtons from "../../components/common/ActionButtons";
import ModalBackground from "../../components/common/ModalBackground";
import {
  getBasicFilteringOptions,
  getClearBasicModel,
} from "../../common/EntitiesManagement";
import AddOrEditBasicEntity from "../../components/common/AddOrEditBasicEntity";
import ErrorMessage from "../../components/common/ErrorMessage";
import DeleteConfirmationMessage from "../../components/common/DeleteConfirmationMessage";
import TableComponent from "../../components/common/TableComponent";
import { generateNoDataValues, getFiltersInfo } from "../../common/utils";
import ValueOrNoDataLabel from "../../components/common/ValueOrNoDataLabel";
import EntityPrinter from "../../components/common/EntityPrinter";

export default function VehicleType() {
  const {
    vehicleTypeModel,
    setVehicleTypeModel,
    vehicleTypeData,
    VEHICLE_TYPE_ENDPOINT,
    config,
    pageIndexVehicleType,
    setPageIndexVehicleType,
    toggleVisibleModalConfirm,
    deleteEntity,
    createEntity,
    updateEntity,
    basicFilterData,
    setBasicFilterOptions,
  } = useGlobalContext();
  const { id } = useParams();
  const [vehicleTypeDataFiltered, setVehicleTypeDataFiltered] = useState(
    vehicleTypeData.Data
  );
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [showPrinterModal, setShowPrinterModal] = useState(false);

  useEffect(() => {
    setBasicFilterOptions(getBasicFilteringOptions());
  }, []);

  useEffect(() => {
    if (id) {
      setShowEditModal(true);
      const operationalDataObj = vehicleTypeData.Data.find(
        (operationalAreaObj) => operationalAreaObj.Id === Number(id)
      );
      setVehicleTypeModel(operationalDataObj);
    }
  }, [id]);

  useEffect(() => {
    setVehicleTypeDataFiltered(vehicleTypeData.Data);
  }, [vehicleTypeData]);

  const validateOperationResult = (result, isDelete) => {
    setVehicleTypeModel(getClearBasicModel());

    if (!result.ok) {
      setErrorMessage(JSON.stringify(result.data));
      setShowErrorModal(true);
      return;
    }

    mutate([VEHICLE_TYPE_ENDPOINT, config]);
    if (isDelete) {
      if (pageIndexVehicleType !== 0 && vehicleTypeModel.Data.length === 1) {
        setPageIndexVehicleType(pageIndexVehicleType - 1);
      }
    }
    window.location.reload(false);
  };

  const handleDeleteRequest = async (itemId) => {
    const deleteResult = await deleteEntity(
      `${`${VEHICLE_TYPE_ENDPOINT}/${itemId}`}`,
      config
    );
    toggleVisibleModalConfirm();
    validateOperationResult(deleteResult, true);
  };

  const handleUpdateRequest = async (data) => {
    const updateResult = await updateEntity(
      `${`${VEHICLE_TYPE_ENDPOINT}/${data.Id}`}`,
      data,
      config
    );
    validateOperationResult(updateResult, false);
  };

  const handleCreateRequest = async (data) => {
    const updateResult = await createEntity(
      `${`${VEHICLE_TYPE_ENDPOINT}`}`,
      data,
      config
    );
    validateOperationResult(updateResult, false);
  };

  const disableSavechanges = () =>
    !vehicleTypeModel?.Name ||
    vehicleTypeModel?.Name.replaceAll(" ", "").length <= 0;

  const getColumnTitles = () => {
    return [
      localize.name,
      localize.description,
      localize.modification_date,
      localize.modified_by,
    ];
  };

  return (
    <div className="lateral_padding">
      {showErrorModal && (
        <ErrorMessage
          message={errorMessage}
          title={localize.error_unexpected}
          showModal={showErrorModal}
          setShowModal={setShowErrorModal}
        />
      )}

      {showPrinterModal && (
        <EntityPrinter
          closeCallback={() => setShowPrinterModal(false)}
          report_title={localize.vehicle_types}
          report_subtitle={getFiltersInfo(basicFilterData)}
          url={VEHICLE_TYPE_ENDPOINT}
          filters={basicFilterData ?? []}
          columns={getColumnTitles()}
          objectTypeName="vehicle_type"
        />
      )}

      {showEditModal && (
        <ModalBackground
          isDrawerMode
          hasRequiredValues
          showModal={showEditModal}
          setShowModal={setShowEditModal}
          title={localize.edit_vehicle_type}
          isEditForm
          onClickToSubmit={() => {
            handleUpdateRequest(vehicleTypeModel);
            setShowEditModal(false);
            setVehicleTypeModel(getClearBasicModel());
          }}
          onClickToCancel={() => {
            setShowEditModal(false);
            setVehicleTypeModel(getClearBasicModel());
          }}
          Component={
            <AddOrEditBasicEntity
              customWidth="100%"
              nameMandatory
              model={vehicleTypeModel}
              setModel={setVehicleTypeModel}
            />
          }
          validators={disableSavechanges()}
        />
      )}

      <Card
        variant="outlined"
        style={{
          margin: "16px",
          boxShadow:
            "rgba(60, 64, 67, 0.3) 0px 1px 2px 0px, rgba(60, 64, 67, 0.15) 0px 1px 3px 1px",
        }}
      >
        <CardContent>
          <PageTitleSubtitle
            title={localize.vehicle_type}
            subtitle={localize.vehicle_type_description}
          />

          <SearcherAndAdd
            printButtonMethod={() => setShowPrinterModal(true)}
            entityName="vehicle_type"
            handleAddBtn={() => setShowAddModal(true)}
            modal={
              <ModalBackground
                isDrawerMode
                hasRequiredValues
                showModal={showAddModal}
                setShowModal={setShowAddModal}
                title={localize.new_vehicle_type}
                onClickToSubmit={() => {
                  handleCreateRequest(vehicleTypeModel);
                  setShowAddModal(false);
                  setVehicleTypeModel(getClearBasicModel());
                }}
                onClickToCancel={() => {
                  setShowAddModal(false);
                  setVehicleTypeModel(getClearBasicModel());
                }}
                Component={
                  <AddOrEditBasicEntity
                    customWidth="100%"
                    nameMandatory
                    model={vehicleTypeModel}
                    setModel={setVehicleTypeModel}
                  />
                }
                validators={disableSavechanges()}
              />
            }
          />
        </CardContent>
      </Card>

      <DeleteConfirmationMessage
        entityName={vehicleTypeModel.Name}
        entityType={localize.vehicle_type}
        onClickForCancel={() => {
          toggleVisibleModalConfirm();
          setVehicleTypeModel(getClearBasicModel());
        }}
        onClickForConfirm={() => {
          handleDeleteRequest(vehicleTypeModel.Id);
          setVehicleTypeModel(getClearBasicModel());
        }}
      />

      <TableComponent
        pagination={
          <PagePagination
            pageIndex={pageIndexVehicleType}
            setPageIndex={setPageIndexVehicleType}
            dataArray={vehicleTypeData}
          />
        }
        firstColumnTitle={localize.actions}
        columnTitles={getColumnTitles()}
        results={
          vehicleTypeData.Data?.length !== 0
            ? vehicleTypeDataFiltered.map((item) => ({
                key: item.Id,
                data: [
                  <div>
                    <ActionButtons
                      onClickForEditButton={() => {
                        setVehicleTypeModel(item);
                        setShowEditModal(true);
                      }}
                      onclickForDeleteButton={() => {
                        setVehicleTypeModel(item);
                        toggleVisibleModalConfirm();
                      }}
                    />
                  </div>,
                  item.Name,
                  <ValueOrNoDataLabel data={item.Description} />,
                  new Date(item.ModificationDate).toLocaleString(undefined, {
                    year: "numeric",
                    month: "2-digit",
                    day: "2-digit",
                    hour: "2-digit",
                    minute: "2-digit",
                    second: "2-digit",
                  }),
                  item.CreatedBy,
                ],
              }))
            : [generateNoDataValues(5)]
        }
      />
    </div>
  );
}
